import axios from 'axios'
import type {
    InvestorActivationRequest,
    InvestorActivationResponse,
    InvestorSignInRequest,
    InvestorSignInResponse,
    CompanyProfilesData,
} from '@/types/auth'
import { API_BASE_URL_V1 } from '@/endpoints'

export const activateInvestorAccount = async (
    activationData: InvestorActivationRequest,
): Promise<InvestorActivationResponse> => {
    try {
        const formData = new FormData()

        formData.append('Password', activationData.password)
        formData.append('Token', activationData.token)
        formData.append('FirstName', activationData.firstName)
        formData.append('LastName', activationData.lastName)
        formData.append('PhoneNumber', activationData.phoneNumber)
        formData.append('AddressLine1', activationData.addressLine1)
        formData.append('AddressPostcode', activationData.addressPostcode)
        formData.append('AddressCity', activationData.addressCity)
        formData.append('AddressCountryId', activationData.addressCountryId)

        const response = await axios.post(`${API_BASE_URL_V1}/investors/activate`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })

        return {
            success: true,
            data: response.data,
        }
    } catch (error: unknown) {
        console.error('Investor activation failed:', error)

        const errorMessage =
            error instanceof Error && 'response' in error
                ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
                : 'Account activation failed. Please try again.'

        return {
            success: false,
            message: errorMessage,
        }
    }
}

export const signInInvestor = async (
    signInData: InvestorSignInRequest,
): Promise<InvestorSignInResponse> => {
    try {
        const formData = new FormData()
        formData.append('EmailAddress', signInData.email)
        formData.append('Password', signInData.password)

        const response = await axios.post(`${API_BASE_URL_V1}/investors/sign-ins`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        })
        if (response.data.isSuccess) {
            return {
                isSuccess: true,
                data: response.data.data,
                messages: response.data.messages || [],
            }
        } else {
            return {
                isSuccess: false,
                messages: response.data.messages || ['Sign-in failed'],
            }
        }
    } catch (error: unknown) {
        console.error('Investor sign-in failed:', error)

        const errorMessage =
            error instanceof Error && 'response' in error
                ? (error as { response?: { data?: { message?: string } } }).response?.data?.message
                : 'Sign-in failed. Please check your credentials and try again.'

        return {
            isSuccess: false,
            messages: [
                errorMessage || 'Sign-in failed. Please check your credentials and try again.',
            ],
        }
    }
}

/**
 * Fetch company profiles for investors (no typed model yet — returns any)
 */
export type GetCompanyProfilesResult =
    | { isSuccess: true; data: CompanyProfilesData }
    | { isSuccess: false; error: string }

export const getCompanyProfiles = async (
    accessToken?: string | null,
): Promise<GetCompanyProfilesResult> => {
    try {
        const getCookie = (name: string): string | null => {
            const nameEQ = name + '='
            const cookies = document.cookie.split(';')
            for (let i = 0; i < cookies.length; i++) {
                let cookie = cookies[i]
                while (cookie.charAt(0) === ' ') cookie = cookie.substring(1, cookie.length)
                if (cookie.indexOf(nameEQ) === 0)
                    return cookie.substring(nameEQ.length, cookie.length)
            }
            return null
        }

        const token = accessToken ?? getCookie('x-member-authorization')

        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (token) {
            headers['x-fastinvestor-member-authorization'] = `Bearer ${token}`
        }

        const response = await axios.get(`${API_BASE_URL_V1}/investors/company-profiles`, {
            headers,
        })

        // API wraps payload in { isSuccess, messages, data }
        const payloadData = response?.data?.data as CompanyProfilesData | undefined
        if (!payloadData) {
            return { isSuccess: false, error: 'No data in response' }
        }

        return { isSuccess: true, data: payloadData }
    } catch (error: unknown) {
        console.error('Failed to fetch company profiles:', error)
        const errorMessage =
            error instanceof Error && 'message' in error
                ? (error as Error).message
                : 'Failed to fetch company profiles'
        return { isSuccess: false, error: errorMessage }
    }
}
